import { useCallback, useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";

import SquareButton from "../Buttons/SquareButton";
import { EmailField, validateEmail } from "../EmailField/EmailField";
import { ExclamationMark, IconLoading } from "../Icons";
import styles from "./Form.module.scss";

export const TEST_IDS = {
    apiErrorMsg: "job-alerts-contextual__api-error",
    inputEmail: "job-alerts-contextual__input-email",
    submitButton: "job-alerts-contextual__btn-submit",
};

type FormFieldValues = {
    email: string;
    newsletter: boolean;
};

type JobAlertsContextualFormProps = {
    filterProps?: Record<string, string>;
    greeting?: string | React.ReactNode | null;
    onFormError?: (error?: any) => void;
    onFormSubmit?: (formData?: FormFieldValues) => Promise<boolean>;
    onFormSuccess?: (formData?: FormFieldValues) => void;
    title?: string;
};

const initialValues: FormFieldValues = {
    email: "",
    newsletter: true,
};

const validateForm = (values: FormFieldValues) => {
    const errors: { [k: string]: string } = {};

    if (!values.email) {
        errors.email = "Email Required";
    } else if (!validateEmail(values.email)) {
        errors.email = "Invalid email address";
    }

    return errors;
};

const LocationLabel = ({ location }: { location?: string }) => {
    const isRemote = !!location && ["remote / flexible", "flexible / remote"].includes(location.toLowerCase());
    const prefix = isRemote ? "that are" : "in";

    return location ? <> {prefix} <strong>{location}</strong></> : null;
}

const JobAlertsContextualForm = ({
    filterProps = {},
    greeting,
    onFormError = () => {},
    onFormSubmit = () => Promise.resolve(true),
    onFormSuccess = () => {},
    title = "Want more jobs like this?",
}: JobAlertsContextualFormProps) => {
    const { category, location } = filterProps;
    const [isSubmitting, setSubmitting] = useState<boolean>(false);
    const [showApiError, setShowApiError] = useState<boolean>(false);

    const handleFormSubmit = useCallback(async (values: FormFieldValues) => {
        setShowApiError(false);
        setSubmitting(true);
        
        onFormSubmit(values).then((isSuccess: boolean) => {        
            if (isSuccess) {
                onFormSuccess(values);
            } else {
                setSubmitting(false);
                setShowApiError(true);
                onFormError();
            }
        })
        .catch((e: Error) => {
            console.warn(e);
            setSubmitting(false);
            setShowApiError(true);
            onFormError(e);
        });
    }, [onFormError, onFormSubmit, onFormSuccess]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.innerContainer}>
                {showApiError && (
                    <div className={styles.errorBlock} data-testid={TEST_IDS.apiErrorMsg}>
                        <ExclamationMark title="Warning"/>
                        <span role="status">Error: Oops, looks like something went wrong. Please try again.</span>
                    </div>
                )}
                <h3 className={styles.heading}>{title}</h3>
                {greeting !== null && (
                    <p className={styles.greeting}>
                        {greeting || (
                            <>
                                We&rsquo;ll send you jobs that match this search criteria.{" "}
                                Get {!!category && <strong>{category}</strong>} jobs
                                <LocationLabel location={location} /> delivered to your inbox every week.
                            </>
                        )}
                    </p>
                )}
                <Formik
                    enableReinitialize={true}
                    initialValues={initialValues}
                    onSubmit={handleFormSubmit}
                    validate={validateForm}
                >
                    {({
                        errors,
                        handleChange,
                        handleSubmit,
                        setTouched,
                        touched,
                        values,
                    }) => (
                        <Form
                            onSubmit={handleSubmit}
                            className={styles.form}
                            noValidate={true}
                            aria-label="Newsletter Sign Up Form"
                        >
                            <div className={styles.formFieldGroup}>
                                <div className={styles.inputFieldsGroup}>
                                    <label className={styles.inputLabel} htmlFor={TEST_IDS.inputEmail}>Enter email</label>
                                    <EmailField
                                        className={styles.emailField}
                                        data-testid={TEST_IDS.inputEmail}
                                        errorClassName={styles.errorGroup}
                                        errorMsgClassName={styles.errorMsg}
                                        fieldErrors={errors.email && touched.email ? [errors.email] : []}
                                        id={TEST_IDS.inputEmail}
                                        inputClassName={styles.emailInput}
                                        name="email"
                                        onChange={(e) => {
                                            if (errors) setTouched({}, false);
                                            handleChange(e);
                                        }}
                                        outlineClassName={styles.emailFieldOutline}
                                        placeholder="Enter Email"
                                        type="email"
                                        value={values.email}
                                        withIcon
                                    />
                                </div>
                                <label className={styles.checkboxContainer}>
                                    <Field
                                        checked={values.newsletter}
                                        className={styles.inputCheck}
                                        name="newsletter"
                                        onChange={handleChange}
                                        type="checkbox"
                                    />
                                    <span className={styles.checkmark} />
                                    <span className={styles.checkboxLabel}>
                                        Send me The Muse newsletters for the best in career advice and&nbsp;job&nbsp;search&nbsp;tips.
                                    </span>
                                </label>
                                <SquareButton
                                    color="secondary-100"
                                    data-testid={TEST_IDS.submitButton}
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={styles.submitButton}
                                >
                                    Get Jobs!
                                    {isSubmitting && <IconLoading />}
                                </SquareButton>
                            </div>
                        </Form>
                    )}
                </Formik>
                <p className={styles.tos}>
                    {"By signing up, you agree to our "}
                    <a href="/user/terms" target="_blank">Terms of Service</a>
                    {" & "}
                    <a href="/user/privacy" target="_blank">Privacy Policy</a>
                    .
                </p>
            </div>
        </div>
    );
};

JobAlertsContextualForm.displayName = "JobAlertsContextualForm";

export { JobAlertsContextualForm as default };
export type { JobAlertsContextualFormProps, FormFieldValues };
